import React, {ComponentProps, useCallback, useMemo} from "react";
import {Manager, Reference, Popper} from "react-popper";
import {theme, Icon, Toggle} from "@reside/ui";
import {TreeSelect, Props as TreeSelectProps} from "../tree-select/TreeSelect";
import {summarizeTree} from "../tree-select/helpers";
import {
  DropdownMenu,
  Trigger,
  TriggerIconWrapper,
  TriggerLabel,
  TriggerValue,
} from "./styles";

type Props = Readonly<{
  triggerLabel: string;
  summarizeTree: typeof summarizeTree;
  placement?: ComponentProps<typeof Popper>["placement"];
  hideTrigger?: boolean;
  autoComplete?: string;
  withSearch?: boolean;
  withoutFacilityName?: boolean;
}> &
  TreeSelectProps;

export const DropdownTreeSelect = ({
  checkedKeys,
  EmptySearch,
  hideTrigger = false,
  onCheck,
  hasOnlyOne,
  placement = "bottom",
  searchPlaceholder,
  summarizeTree,
  treeData,
  triggerLabel,
  autoComplete,
  withSearch = true,
  withApplyFiltersButton = false,
  withoutFacilityName = false, // if true, only show number of selected facilities
}: Props) => {
  const leafs = useMemo(
    () => treeData.reduce((keys, node) => [...keys, ...node.children], []),
    [treeData],
  );
  const allLeafKeys: ReadonlyArray<string> = useMemo(
    () => leafs.map(({key}) => key),
    [leafs],
  );

  const checkedLeafKeys = useMemo(
    () =>
      (checkedKeys || allLeafKeys).filter(
        key => !treeData.find(node => node.key === key),
      ),
    [checkedKeys, allLeafKeys, treeData],
  );

  const handleCheck = useCallback(
    (newCheckedKeys: string[], newCheckedLeafKeys: string[]) => {
      onCheck(newCheckedKeys, newCheckedLeafKeys);
    },
    [onCheck],
  );

  return (
    <Toggle>
      {({isOpened, setIsOpened, outsideClickIgnoreClass}) => (
        <Manager>
          <Reference>
            {({ref}) => (
              <Trigger
                ref={ref}
                onClick={
                  hideTrigger ? () => null : () => setIsOpened(!isOpened)
                }
              >
                <div
                  className="dropdown-tree-select-trigger"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    width: "fit-content",
                  }}
                >
                  {triggerLabel && <TriggerLabel>{triggerLabel}</TriggerLabel>}
                  <TriggerValue
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      minWidth: 0, // allows flex item to shrink
                    }}
                  >
                    {summarizeTree(checkedLeafKeys, leafs, withoutFacilityName)}
                  </TriggerValue>
                </div>
                {!hideTrigger && (
                  <TriggerIconWrapper>
                    <Icon
                      name={isOpened ? "up" : "down"}
                      size={12}
                      color={theme.color.primary}
                    />
                  </TriggerIconWrapper>
                )}
              </Trigger>
            )}
          </Reference>
          {isOpened && (
            <Popper placement={placement}>
              {({ref, style}) => (
                <DropdownMenu
                  ref={ref}
                  style={style}
                  className={outsideClickIgnoreClass}
                >
                  <TreeSelect
                    treeData={treeData}
                    searchPlaceholder={searchPlaceholder}
                    EmptySearch={EmptySearch}
                    checkedKeys={checkedKeys}
                    onCheck={(checkedKeys, checkedLeafKeys) => {
                      handleCheck(checkedKeys, checkedLeafKeys);
                      if (hasOnlyOne) {
                        setIsOpened(false);
                      }
                    }}
                    hasOnlyOne={hasOnlyOne}
                    autoComplete={autoComplete}
                    withSearch={withSearch}
                    withApplyFiltersButton={withApplyFiltersButton}
                  />
                </DropdownMenu>
              )}
            </Popper>
          )}
        </Manager>
      )}
    </Toggle>
  );
};

DropdownTreeSelect.defaultProps = {
  summarizeTree,
};
